import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

/** Fire-and-forget sign-in attempt logger — never blocks auth flow. */
function logSignInAttempt(
  email: string,
  success: boolean,
  errorMessage: string | null,
  signInMethod: string,
  clientPlatform: string,
) {
  supabase.functions
    .invoke('log-sign-in', {
      body: { email, success, errorMessage, signInMethod, clientPlatform },
    })
    .catch(() => {})
}

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (
    email: string,
    password: string,
    name: string,
    captchaToken?: string,
    dateOfBirth?: string,
    youthCategory?: string,
  ) => Promise<{ error: Error | null; needsConfirmation: boolean }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: Error | null }>
  updatePassword: (password: string) => Promise<{ error: Error | null }>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const initialLoadDone = useRef(false)

  useEffect(() => {
    let mounted = true

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return
      setSession(s)
      setUser(s?.user ?? null)
      setLoading(false)
      initialLoadDone.current = true
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      if (!mounted) return
      // Only update loading state from the listener after initial getSession() has resolved.
      // This prevents a race where onAuthStateChange fires with INITIAL_SESSION (null)
      // before getSession() has a chance to restore/refresh the token.
      setSession(s)
      setUser(s?.user ?? null)
      if (initialLoadDone.current) {
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (!error) {
      // Increment MFA grace period login count and set grace start if not already set
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser()
      if (currentUser) {
        const metadata = currentUser.user_metadata ?? {}
        const loginCount = ((metadata.mfa_login_count as number) ?? 0) + 1
        const graceStart = (metadata.mfa_grace_start as string) ?? new Date().toISOString()
        await supabase.auth.updateUser({
          data: { mfa_login_count: loginCount, mfa_grace_start: graceStart },
        })
      }
    }

    // Log sign-in attempt (fire-and-forget — never blocks auth flow)
    logSignInAttempt(email, !error, error?.message ?? null, 'email_password', 'web')

    return { error: error ? new Error(error.message) : null }
  }

  const signUp = async (
    email: string,
    password: string,
    name: string,
    captchaToken?: string,
    dateOfBirth?: string,
    youthCategory?: string,
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          ...(dateOfBirth ? { date_of_birth: dateOfBirth } : {}),
          ...(youthCategory ? { youth_category: youthCategory } : {}),
        },
        ...(captchaToken ? { captchaToken } : {}),
      },
    })
    // If no session returned, the user needs to confirm their email first
    const needsConfirmation = !error && !data.session
    return { error: error ? new Error(error.message) : null, needsConfirmation }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    // Clear MFA session timestamp on sign-out (60-minute idle session).
    // Trusted device (`zero_mfa_trusted_device`) is intentionally preserved
    // so "Remember this device for 30 days" persists across sign-in/sign-out
    // cycles. Users can revoke device trust from MFA Settings if needed.
    try {
      localStorage.removeItem('zero_mfa_last_active')
    } catch {
      // Storage may not be available
    }
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/auth',
    })
    return { error: error ? new Error(error.message) : null }
  }

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    return { error: error ? new Error(error.message) : null }
  }

  return (
    <AuthContext.Provider
      value={{ user, session, loading, signIn, signUp, signOut, resetPassword, updatePassword }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
